import { useEffect, useRef, useState, type FunctionComponent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Modal, Space, Typography, message } from "antd";

import { type ChallengeAudienceSpec } from "@fitness-app/data-models/entities/Challenge";

import { TraineeTargetingPanel, useTraineeTargeting } from "~/components/TraineeTargeting";
import { emptyTraineeTargetSpec, isFilterActive, type TraineeTargetSpec } from "~/components/TraineeTargeting/types";
import { supabase } from "~/store/initializeStore";

type Props = {
  open: boolean;
  challengeId: string;
  audience: ChallengeAudienceSpec | null;
  onClose: () => void;
};

/**
 * Audience segment for OPEN self-join (no code): who may see and join this challenge from the app.
 * Stores the targeting spec on challenge.audience — enforcement happens server-side in RLS
 * (challenge_audience_matches), this panel is authoring + preview only. Empty spec saves as NULL
 * (= the whole org may join).
 */
export const ChallengeAudienceModal: FunctionComponent<Props> = ({ open, challengeId, audience, onClose }) => {
  const queryClient = useQueryClient();
  const [spec, setSpec] = useState<TraineeTargetSpec>(emptyTraineeTargetSpec);

  // Seed once per open — background refetches must not clobber in-progress edits.
  const seededRef = useRef(false);

  useEffect(() => {
    if (!open) {
      seededRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (open && !seededRef.current) {
      seededRef.current = true;
      setSpec(audience ? { ...emptyTraineeTargetSpec, ...(audience as unknown as TraineeTargetSpec) } : emptyTraineeTargetSpec);
    }
  }, [open, audience]);

  const { resolved, isLoading } = useTraineeTargeting(spec, open);

  const saveMutation = useMutation({
    mutationFn: async (nextAudience: ChallengeAudienceSpec | null) => {
      const { error } = await supabase
        .from("challenge")
        .update({ audience: nextAudience } as never)
        .eq("id", challengeId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void message.success("Grupa docelowa zapisana");
      void queryClient.invalidateQueries({ queryKey: ["challenge-detail", challengeId] });
      onClose();
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się zapisać grupy docelowej: ${error.message}`);
    },
  });

  const hasAnySelection = isFilterActive(spec) || spec.manualIds.length > 0 || spec.excludedIds.length > 0;

  return (
    <Modal
      title={<Typography.Text>Grupa docelowa dołączania</Typography.Text>}
      open={open}
      onCancel={onClose}
      width={680}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onClose}>Anuluj</Button>
          <Button danger disabled={!audience} loading={saveMutation.isLoading} onClick={() => saveMutation.mutate(null)}>
            Wyczyść (cała organizacja)
          </Button>
          <Button
            type="primary"
            loading={saveMutation.isLoading}
            onClick={() => saveMutation.mutate(hasAnySelection ? (spec as unknown as ChallengeAudienceSpec) : null)}
          >
            Zapisz segment
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary">
        Kto może samodzielnie dołączyć do tego wyzwania w aplikacji (przy włączonym samodzielnym dołączaniu).
        Pusty segment = wszystkie klientki organizacji. Egzekwowane po stronie serwera.
      </Typography.Paragraph>
      <TraineeTargetingPanel value={spec} onChange={setSpec} />
      <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
        {isLoading ? "Liczenie dopasowanych klientek…" : `Pasujące klientki: ${resolved.length}`}
      </Typography.Paragraph>
    </Modal>
  );
};

export default ChallengeAudienceModal;
